import React, { useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import classNames from "classnames";

import CountDown from "@components/countdown/countdown";
import EmptyQueryHandler from "@components/empty-query-handler/empty-query-handler";
import HtmlSpan, { renderCost } from "@components/html-span/HtmlSpan";
import CharacterCursor from "@components/icons/characters/cursor-svg";
import CharactersSvg from "@components/icons/characters-svg";
import ChevronRightSVG from "@components/icons/chevron-right-svg";
import PlusSVG from "@components/icons/plus-svg";
import ButtonCheckbox from "@components/inputs/checkbox";
import RemoveAdsLink from "@components/remove-ads-link/remove-ads-link";
import WrapperItemCourse from "@components/wrapper-item-course/wrapper-item-course";

import { useAppDispatch, useAppSelector } from "@hooks/use-store";
import {
  setActiveCharacterName,
  setIsAdsScriptsReloaded,
  showKeyboardUpload,
} from "@store/app/app.slice";
import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import { addDividerOnModal, addStyle, pluralizeDays } from "@helpers/helpers";
import { CHARACTERS } from "@constants/characters.constant";
import { CharactersTypes, IUser, Locales } from "@typings/global.types";

import {
  useGetCharactersQuery,
  useUpdateCharactersMutation,
} from "../../api/characters";
import { useGetCourseQuery } from "../../api/course";
import { useGetExerciseEntrypointQuery } from "../../api/exercise-entrypoint";
import { useUpdateUserInfoMutation } from "../../api/user-info";

import DisableAdsBlock from "./disable-ads-block";

function Settings() {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const intl = useIntl();
  const { locale } = intl;
  const navigate = useNavigate();

  const { showKeyboard, activeCharacterName } = useAppSelector(
    (state) => state.app,
  );

  const { data } = useGetExerciseEntrypointQuery({
    exerciseId: 0,
  });

  const { data: chatactersData, isLoading: isCharactersLoading } =
    useGetCharactersQuery();

  const { data: lessonsData, isLoading: isLessonsLoading } = useGetCourseQuery({
    courseId: data!.user.courseId,
  });

  const modalBody = useRef<HTMLDivElement>(null);
  const hr = useRef<HTMLHRElement>(null);

  const [editUser, { isSuccess, isError, error }] = useUpdateUserInfoMutation();
  const [
    updateCharacters,
    {
      isSuccess: charactersIsSuccess,
      isError: charactersIsError,
      error: charactersError,
    },
  ] = useUpdateCharactersMutation();

  const { closeModal } = useOutletContext<{
    closeModal?: () => void;
    minModalHeight?: number;
    setMinModalHeight?: (height: number) => void;
  }>();

  const handleChangeCharacter = (name: CharactersTypes | null, id?: number) => {
    dispatch(setActiveCharacterName(name));
    if (!name) {
      if (data?.user.gameMode) {
        editUser({
          params: { gameMode: false },
        });
      }
    } else {
      if (!data?.user.gameMode) {
        editUser({
          params: {
            gameMode: true,
          },
        });
      }
      if (name !== activeCharacterName) {
        updateCharacters({ params: { characterId: Number(id) } });
      }
    }
  };

  const handleShowKeboard = (): void => {
    dispatch(showKeyboardUpload(!showKeyboard));
    editUser({
      params: { showKeyboard: !showKeyboard },
    });
    dispatch(setIsAdsScriptsReloaded(true));
  };

  useEffect(() => {
    addDividerOnModal(modalBody, hr);
    const close = (event: any) => {
      setTimeout(() => {
        if (event.keyCode === 27) {
          if (closeModal) closeModal();
        }
      }, 250);
    };
    window.addEventListener("keyup", close);

    return () => window.removeEventListener("keyup", close);
  }, [closeModal]);

  return (
    <EmptyQueryHandler isLoading={!lessonsData || !chatactersData}>
      <div className='modal-header p-4 pb-0 md:p-6 md:pb-0'>
        <div className='relative text-center'>
          <h3>
            <FormattedMessage id='settings' />
          </h3>
        </div>
      </div>
      <hr
        className='mx-4 mt-1 border-t-2 border-transparent transition-all md:mx-6'
        ref={hr}
      />
      <div className='modal-body p-4 pt-0 md:p-6 md:pt-0' ref={modalBody}>
        <div className='pb-1 capitalize'>
          <FormattedMessage id='hero' />:{" "}
          <b>
            <FormattedMessage id={activeCharacterName ?? "cursor"} />
          </b>
        </div>
        <div className='-mx-1 mb-2 flex flex-wrap md:mb-3'>
          <div className='p-1'>
            <button
              type='button'
              onClick={() => handleChangeCharacter(null)}
              data-name='cursor'
              className={classNames(
                "mb-lg dark:hover:border-brand-dark-primary flex h-[70px] w-[70px] cursor-pointer items-center justify-center rounded-full border-2 border-transparent transition-border duration-300 ease-in-out hover:border-brand-primary",
                {
                  "dark:!border-brand-dark-primary !border-brand-primary":
                    !activeCharacterName,
                },
              )}
            >
              <CharacterCursor width={4} height={35} />
            </button>
          </div>
          {chatactersData?.characters.map((character) =>
            character.active && character.purchased ? (
              <div className='p-1' key={character.id}>
                <button
                  type='button'
                  onClick={() =>
                    handleChangeCharacter(character.name, character.id)
                  }
                  className={classNames(
                    "mb-lg dark:hover:border-brand-dark-primary flex h-[70px] w-[70px] cursor-pointer items-center justify-center rounded-full border-2 border-transparent transition-border duration-300 ease-in-out hover:border-brand-primary",
                    {
                      "dark:!border-brand-dark-primary !border-brand-primary":
                        character.name === activeCharacterName,
                    },
                  )}
                  title={character.name}
                >
                  <div className='max-w-[3.375rem]'>
                    <div className='overflow-hidden rounded-full bg-white'>
                      <img
                        src={CHARACTERS[character.name].gif}
                        alt={character.name}
                        className='p-1'
                      />
                    </div>
                  </div>
                </button>
              </div>
            ) : null,
          )}
          <div className='p-1'>
            <button
              id='tutor_settings_heroes_store_btn'
              type='button'
              onClick={() =>
                navigate(formatUrlWithLocale("/typing-tutor/store/"))
              }
              className='mb-lg dark:bg-bg-dark-secondary relative flex h-[70px] w-[70px] cursor-pointer items-center justify-center rounded-full bg-bg-secondary'
            >
              <PlusSVG width={35} height={35} />
              <CharactersSvg
                classNameProps='absolute -right-[7px] -bottom-[10px]'
                width={48}
                height={32}
              />
            </button>
          </div>
        </div>
        <hr className='dark:border-brd-dark-primary mt-3 border-t-2 border-brd-primary md:mt-4' />
        <div className='mt-3 md:mt-4'>
          <ButtonCheckbox
            classNameLabel='justify-between'
            onChange={handleShowKeboard}
            text={formatMessage({ id: "show-keyboard" })}
            id='showKeyboard'
            checked={showKeyboard}
          />
        </div>
        <hr className='dark:border-brd-dark-primary mt-3 border-t-2 border-brd-primary md:mt-4' />
        {lessonsData ? (
          <div className='mt-3 md:mt-4'>
            <Link
              to={formatUrlWithLocale("/typing-tutor/select-exercise/")}
              className='not-prose relative flex justify-between'
            >
              <WrapperItemCourse course={lessonsData.course} className='mr-9' />
              <ChevronRightSVG
                classNameProps='fill-icon-primary dark:fill-icon-dark-primary shrink-0 mt-0.5 mr-1 ml-2'
                height={22}
                width={14}
              />
            </Link>
          </div>
        ) : null}

        {data && !data?.user.plus && !window.reactApp.haveLicence && (
          <DisableAdsBlock
            isPromotion={data.user.isPromotion}
            isSale={data.user.isSale}
            regDatetime={data.user.regDatetime}
            isBirthday={window.reactApp.isBirthDay}
          />
        )}
      </div>
    </EmptyQueryHandler>
  );
}

export default Settings;
